<template>
  <div class="app-page friend-page">
    <div class="friend-header">
      <h2 class="page-title">我的好友</h2>
      <div class="header-actions">
        <router-link to="/friend/applies" class="apply-btn">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>
          好友申请
          <span class="badge" v-if="applyCount > 0">{{ applyCount > 99 ? '99+' : applyCount }}</span>
        </router-link>
        <button class="add-friend-btn" @click="showAddDialog = true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          添加好友
        </button>
      </div>
    </div>

    <!-- 搜索好友 -->
    <div class="search-section" v-if="showAddDialog">
      <div class="search-row">
        <input
          v-model="searchKeyword"
          type="text"
          placeholder="输入用户ID搜索..."
          class="search-input"
          @keyup.enter="searchUsers"
        />
        <button class="search-btn" @click="searchUsers">搜索</button>
        <button class="cancel-btn" @click="showAddDialog = false; searchKeyword = ''; searchResults = []">取消</button>
      </div>
      <div v-if="searchResults.length > 0" class="search-results">
        <div v-for="user in searchResults" :key="user.id" class="search-item">
          <img :src="user.avatar || defaultAvatar" class="search-avatar" alt="">
          <div class="search-info">
            <span class="search-name">{{ user.nickname }}</span>
            <span class="search-id">ID: {{ user.id }}</span>
          </div>
          <button
            class="action-btn"
            @click="sendApply(user)"
            :disabled="user.applySent"
          >
            {{ user.applySent ? '已申请' : '添加好友' }}
          </button>
        </div>
      </div>
      <div v-if="searchDone && searchResults.length === 0" class="no-results">
        未找到相关用户
      </div>
    </div>

    <!-- 好友列表 -->
    <div class="friend-list" v-if="friendList.length > 0">
      <div v-for="friend in friendList" :key="friend.friendId" class="friend-item">
        <div class="friend-avatar-wrapper">
          <img :src="friend.avatar || defaultAvatar" class="friend-avatar" alt="">
          <span class="online-dot" :class="{ online: friend.online }"></span>
        </div>
        <div class="friend-info">
          <span class="friend-name">{{ friend.remark || friend.nickname }}</span>
          <span class="friend-status">{{ friend.online ? '在线' : '离线' }}</span>
        </div>
        <div class="friend-actions">
          <button class="chat-btn" @click="startChat(friend)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
            发消息
          </button>
          <button class="game-btn" @click="playGame(friend)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="8" cy="12" r="1.5" fill="currentColor"/><circle cx="16" cy="12" r="1.5" fill="currentColor"/></svg>
            棋牌对战
          </button>
          <button class="remove-btn" @click="removeFriend(friend)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
          </button>
        </div>
      </div>
    </div>

    <div v-else-if="!showAddDialog" class="empty-state">
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#B2BEC3" stroke-width="1.5"><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><path d="M20 8v6M23 11h-6"/></svg>
      <p>还没有好友</p>
      <button class="add-first-btn" @click="showAddDialog = true">添加第一个好友</button>
    </div>

    <!-- 游戏类型选择对话框 -->
    <el-dialog
      v-model="showGameDialog"
      title="🎮 选择游戏"
      width="360px"
      :close-on-click-modal="false"
      :append-to-body="true"
      class="game-picker-dialog"
    >
      <div class="game-picker-list">
        <div class="game-picker-item" @click="sendGameInvite(1, '斗地主')">
          <div class="game-picker-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#e74c3c" stroke-width="1.5"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="8" cy="12" r="1.5" fill="currentColor"/><circle cx="16" cy="12" r="1.5" fill="currentColor"/><path d="M12 2v4M12 18v4M7 8l-2-3M17 8l2-3"/></svg>
          </div>
          <div class="game-picker-info">
            <span class="game-picker-name">斗地主</span>
            <span class="game-picker-desc">经典3人玩法，抢地主决胜负</span>
          </div>
          <svg class="game-picker-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
        </div>
        <div class="game-picker-item" @click="sendGameInvite(2, '挤黑五')">
          <div class="game-picker-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#6C5CE7" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/><path d="M8 3l2 3M16 3l-2 3M6 8l-3 2M21 10l-3 2"/></svg>
          </div>
          <div class="game-picker-info">
            <span class="game-picker-name">挤黑五</span>
            <span class="game-picker-desc">4-5人策略玩法，考验你的牌技</span>
          </div>
          <svg class="game-picker-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import axios from 'axios'
import friendApi from '@/api/friend'
import socketService from '@/api/socket'
import gameApi from '@/api/game'

const router = useRouter()
const defaultAvatar = 'https://picsum.photos/id/1005/200'

const friendList = ref([])
const searchKeyword = ref('')
const searchResults = ref([])
const searchDone = ref(false)
const showAddDialog = ref(false)
const applyCount = ref(0)

// 游戏邀请相关
const showGameDialog = ref(false)
const selectedFriend = ref(null)

const userId = ref(null)

onMounted(() => {
  const stored = localStorage.getItem('userId')
  userId.value = stored ? Number(stored) : null
  if (userId.value) {
    fetchFriendList()
    fetchApplyCount()
  }
})

const fetchFriendList = async () => {
  try {
    const res = await friendApi.getFriendList(userId.value)
    if (res.data.success) {
      friendList.value = res.data.data || []
    }
  } catch (error) {
    console.error('获取好友列表失败:', error)
  }
}

const fetchApplyCount = async () => {
  try {
    const res = await friendApi.getReceivedApplies(userId.value)
    if (res.data.success) {
      applyCount.value = (res.data.data || []).length
    }
  } catch (error) {
    console.error('获取申请数量失败:', error)
  }
}

const searchUsers = async () => {
  if (!searchKeyword.value.trim()) return
  searchDone.value = true
  searchResults.value = []
  try {
    // 通过用户ID搜索
    const res = await axios.get('/user-service/api/v1/users/getById', {
      params: { id: searchKeyword.value.trim() }
    })
    if (res.data.success && res.data.data) {
      const user = res.data.data
      // 不能添加自己
      if (user.id === userId.value) {
        ElMessage.warning('不能添加自己为好友')
        return
      }
      // 检查是否已是好友
      const isFriend = friendList.value.some(f => f.friendId === user.id)
      searchResults.value = [{
        ...user,
        applySent: isFriend
      }]
    } else {
      searchResults.value = []
    }
  } catch (error) {
    console.error('搜索用户失败:', error)
  }
}

const sendApply = async (user) => {
  try {
    const res = await friendApi.sendApply(userId.value, user.id, '你好，想加个好友')
    if (res.data.success) {
      ElMessage.success('好友申请已发送')
      user.applySent = true
    } else {
      ElMessage.warning(res.data.msg || '发送失败')
    }
  } catch (error) {
    ElMessage.error('发送好友申请失败')
  }
}

const startChat = (friend) => {
  router.push({ path: '/', query: { targetUserId: friend.friendId } })
}

const removeFriend = async (friend) => {
  try {
    await ElMessageBox.confirm(`确定要删除好友 ${friend.nickname} 吗？`, '删除好友', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    const res = await friendApi.removeFriend(userId.value, friend.friendId)
    if (res.data.success) {
      ElMessage.success('已删除好友')
      fetchFriendList()
    }
  } catch (error) {
    if (error !== 'cancel' && error !== 'close') {
      console.error('删除好友失败:', error)
    }
  }
}

const playGame = (friend) => {
  selectedFriend.value = friend
  showGameDialog.value = true
}

const sendGameInvite = async (gameType, gameTypeName) => {
  const friend = selectedFriend.value
  if (!friend) return
  showGameDialog.value = false

  const maxPlayers = gameType === 1 ? 3 : 5
  try {
    const res = await gameApi.createRoom(userId.value, gameType, maxPlayers)
    if (!res.data.success) {
      ElMessage.error(res.data.msg || '创建房间失败')
      return
    }

    const roomId = res.data.data.id
    const roomCode = res.data.data.roomCode

    const inviterName = localStorage.getItem('username') || '好友'
    const inviteContent = JSON.stringify({
      gameType,
      gameTypeName,
      roomId,
      roomCode,
      inviterName
    })

    // 确保 WebSocket 已连接
    if (!socketService.isConnected) {
      socketService.connect()
      // 等待连接建立（最多3秒）
      for (let i = 0; i < 30; i++) {
        await new Promise(r => setTimeout(r, 100))
        if (socketService.isConnected) break
      }
    }

    if (socketService.isConnected) {
      await socketService.send({
        senderId: userId.value,
        receiverId: friend.friendId,
        content: inviteContent,
        type: 3,
        sendTime: new Date().toISOString()
      })
      ElMessage.success(`已邀请 ${friend.nickname || friend.friendId} 一起玩${gameTypeName}`)
    } else {
      ElMessage.warning('游戏房间已创建，但消息通道未连接，请从聊天页面发送邀请')
    }

    router.push(`/game/room/${roomId}`)
  } catch (error) {
    ElMessage.error('发送游戏邀请失败')
  }
}
</script>

<style scoped>
.friend-page {
  max-width: 720px;
  margin: 0 auto;
  padding: 24px 16px;
}
.friend-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}
.page-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--app-text-primary);
  margin: 0;
}
.header-actions {
  display: flex;
  gap: 10px;
}
.apply-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: var(--app-radius-full);
  background: var(--app-bg-soft);
  color: var(--app-text-primary);
  text-decoration: none;
  font-size: 0.875rem;
  transition: all 0.2s;
  position: relative;
}
.apply-btn:hover {
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary);
}
.badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #e74c3c;
  color: white;
  font-size: 0.7rem;
  padding: 1px 6px;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
}
.add-friend-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: var(--app-radius-full);
  border: none;
  background: var(--app-color-primary);
  color: white;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}
.add-friend-btn:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}
.search-section {
  background: var(--app-bg-elevated);
  border-radius: var(--app-radius-lg);
  padding: 16px;
  margin-bottom: 20px;
  border: 1px solid var(--app-border-subtle);
}
.search-row {
  display: flex;
  gap: 8px;
}
.search-input {
  flex: 1;
  padding: 10px 14px;
  border: 1px solid var(--app-border-light);
  border-radius: var(--app-radius-md);
  background: var(--app-bg-soft);
  color: var(--app-text-primary);
  font-size: 0.875rem;
  outline: none;
}
.search-input:focus {
  border-color: var(--app-color-primary-light);
}
.search-btn {
  padding: 8px 20px;
  border: none;
  border-radius: var(--app-radius-md);
  background: var(--app-color-primary);
  color: white;
  cursor: pointer;
  font-size: 0.875rem;
}
.cancel-btn {
  padding: 8px 12px;
  border: 1px solid var(--app-border-light);
  border-radius: var(--app-radius-md);
  background: transparent;
  color: var(--app-text-muted);
  cursor: pointer;
  font-size: 0.875rem;
}
.search-results {
  margin-top: 12px;
}
.search-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: var(--app-radius-md);
  background: var(--app-bg-soft);
}
.search-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}
.search-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.search-name {
  font-weight: 600;
  font-size: 0.9375rem;
  color: var(--app-text-primary);
}
.search-id {
  font-size: 0.8rem;
  color: var(--app-text-muted);
}
.action-btn {
  padding: 6px 14px;
  border: 1px solid var(--app-color-primary-light);
  border-radius: var(--app-radius-full);
  background: transparent;
  color: var(--app-color-primary);
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.2s;
}
.action-btn:hover:not(:disabled) {
  background: var(--app-color-primary);
  color: white;
}
.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.no-results {
  text-align: center;
  padding: 20px;
  color: var(--app-text-muted);
  font-size: 0.9rem;
}
.friend-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.friend-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: var(--app-bg-elevated);
  border-radius: var(--app-radius-md);
  transition: all 0.2s;
  border: 1px solid transparent;
}
.friend-item:hover {
  border-color: var(--app-border-subtle);
  box-shadow: var(--app-shadow-card);
}
.friend-avatar-wrapper {
  position: relative;
  flex-shrink: 0;
}
.friend-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
}
.online-dot {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #95a5a6;
  border: 2px solid var(--app-bg-elevated);
}
.online-dot.online {
  background: #2ecc71;
}
.friend-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.friend-name {
  font-weight: 600;
  font-size: 0.9375rem;
  color: var(--app-text-primary);
}
.friend-status {
  font-size: 0.8rem;
  color: var(--app-text-muted);
}
.friend-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}
.chat-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: none;
  border-radius: var(--app-radius-full);
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary);
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.2s;
}
.chat-btn:hover {
  background: var(--app-color-primary);
  color: white;
}
.game-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid var(--app-color-primary-light);
  border-radius: var(--app-radius-full);
  background: transparent;
  color: var(--app-color-primary);
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.2s;
}
.game-btn:hover {
  background: var(--app-color-primary-soft);
  border-color: var(--app-color-primary);
}
.remove-btn {
  padding: 6px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--app-text-muted);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
}
.remove-btn:hover {
  background: rgba(231, 76, 60, 0.1);
  color: #e74c3c;
}
.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: var(--app-text-muted);
}
.empty-state p {
  margin: 16px 0;
  font-size: 1rem;
}
.add-first-btn {
  padding: 10px 24px;
  border: none;
  border-radius: var(--app-radius-full);
  background: var(--app-color-primary);
  color: white;
  cursor: pointer;
  font-size: 0.9rem;
}

/* ── 游戏类型选择对话框 ── */
.game-picker-dialog :deep(.el-dialog__body) {
  padding: 12px 20px 20px;
}

.game-picker-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.game-picker-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: var(--app-radius-md);
  border: 1px solid var(--app-border-light);
  cursor: pointer;
  transition: all 0.2s;
}

.game-picker-item:hover {
  border-color: var(--app-color-primary);
  background: var(--app-color-primary-soft);
  transform: translateX(2px);
}

.game-picker-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--app-bg-soft);
  flex-shrink: 0;
}

.game-picker-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.game-picker-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--app-text-primary);
}

.game-picker-desc {
  font-size: 0.8125rem;
  color: var(--app-text-muted);
}

.game-picker-arrow {
  color: var(--app-text-muted);
  flex-shrink: 0;
}

.game-picker-item:hover .game-picker-arrow {
  color: var(--app-color-primary);
}
</style>
